import React, { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { CalendarClock, TrendingUp, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

function fmtShort(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function RenewalsThisWeek() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/dashboard/renewals-this-week")
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, projected_revenue: 0, items: [] }));
  }, []);

  if (!data) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="text-sm text-gray-500">Loading renewals…</div>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-5"
      data-testid="renewals-week-widget"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline">Renewals This Week</div>
          <div className="flex items-baseline gap-3 mt-1">
            <span
              className="font-heading text-3xl font-bold tabular-nums"
              data-testid="renewals-week-count"
            >
              {data.count}
            </span>
            <span className="text-xs text-gray-500">in next 7 days</span>
          </div>
          {data.pending_count > 0 && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-2 py-0.5"
              data-testid="renewals-week-pending"
            >
              {data.pending_count} awaiting your confirmation
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="overline">Projected</div>
          <div
            className="mt-1 font-heading text-lg font-semibold text-emerald-700 tabular-nums flex items-center gap-1 justify-end"
            data-testid="renewals-week-revenue"
          >
            <TrendingUp size={14} /> {money(data.projected_revenue)}
          </div>
        </div>
      </div>

      {data.items?.length ? (
        <ul className="divide-y divide-gray-100">
          {data.items.map((it) => (
            <li
              key={it.reservation_id}
              className="py-2.5 flex items-center justify-between gap-3"
              data-testid={`renewals-week-item-${it.reservation_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  {it.customer_name || "Unknown"}
                  {it.renewal_pending && (
                    <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200 font-medium">
                      Pending
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {it.vehicle_label || "—"}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-gray-600 flex items-center gap-1 justify-end">
                  <CalendarClock size={11} /> {fmtShort(it.return_datetime)}
                </div>
                <div className="text-sm font-semibold tabular-nums">
                  {money(it.rate_amount)}
                </div>
              </div>
              <Link
                to={`/admin/reservations/${it.reservation_id}`}
                className="shrink-0 text-gray-400 hover:text-gray-900 transition"
                data-testid={`renewals-week-open-${it.reservation_id}`}
                aria-label="Open reservation"
              >
                <ArrowRight size={14} />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">
          No renewals in the next 7 days. Enjoy the calm.
        </p>
      )}
    </div>
  );
}
